export const translations = {
  en: {
    // Header Section
    greeting: "Hi, I'm Ahmed Asakrah 👋",
    tagline: "Software Engineer",
    description: "Full-stack developer specializing in API design and AI-powered applications. Experienced in modern web technologies, cloud platforms, and machine learning solutions. Passionate about creating scalable software architectures and innovative digital experiences that bridge the gap between cutting-edge technology and real-world business needs.",
    downloadCV: "Download CV",
    learnMore: "Learn More",
    scrollDown: "Scroll Down",

    // Navigation
    nav: {
      home: "Home",
      about: "About",
      experience: "Experience",
      projects: "Projects",
      skills: "Skills",
      education: "Education",
      certificates: "Certificates",
      contact: "Contact"
    },

    // About Section
    about: {
      title: "About Me",
      subtitle: "Passionate Software Engineer with expertise in full-stack development and AI technologies",
      professionalSummary: "Professional Summary",
      summary1: "I'm a dedicated Software Engineer with experience in developing, managing, and optimizing software solutions across various domains. Currently working at mVISE AG on the APIX project for Vodafone Group, focusing on RESTful API development with JavaScript, Node.js, and Apigee.",
      summary2: "My expertise spans full-stack development, AI/ML technologies, and cloud computing. I have hands-on experience with modern development frameworks, DevOps practices, and agile methodologies. I'm particularly passionate about AI-powered applications and innovative software solutions.",
      summary3: "When I'm not coding, I enjoy exploring new technologies, contributing to projects, and sharing knowledge through tutoring. I believe in continuous learning and always strive to improve my technical skills and deliver high-quality solutions.",
      personalDetails: "Personal Details",
      location: "Location",
      age: "Age",
      languages: "Languages",
      availability: "Availability",
      locationValue: "Düsseldorf, Germany",
      ageValue: "25 Years",
      languagesValue: "English, German, Arabic",
      availabilityValue: "Open for opportunities",
      features: {
        fullStack: {
          title: "Full-Stack Development",
          description: "Experienced in both frontend and backend technologies with Java, JavaScript, Python, and React.js."
        },
        ai: {
          title: "AI & Machine Learning",
          description: "Working with LLMs, PyTorch, TensorFlow, and Hugging Face for AI-powered solutions."
        },
        api: {
          title: "API Development",
          description: "Specialized in developing and optimizing RESTful APIs using Node.js and Apigee."
        },
        devops: {
          title: "DevOps & Cloud",
          description: "Proficient in DevOps practices with Jenkins, Docker, AWS, and GCP technologies."
        }
      }
    },

    // Experience Section
    experience: {
      title: "Professional Experience",
      subtitle: "My journey in software development and technology",
      workExperience: "Work Experience",
      workSubtitle: "My professional journey in software engineering and development",
      experiences: {
        mvise: {
          position: "Software Developer",
          location: "Düsseldorf, Germany (Hybrid)",
          description: "Currently deployed at Vodafone Group as part of the APIX project. Responsible for developing, managing, and optimizing RESTful APIs using JavaScript, Node.js, and Apigee. Implementing security mechanisms (e.g., HMAC), working with JSON, and utilizing DevOps practices and cloud technologies including AWS Lambda, Amazon S3, and GCP. Working in an agile environment using JIRA and Scrum methodologies. Also involved in WDW-Bosch project using Go and AWS Cloud services."
        },
        tudortmund1: {
          position: "Project Group 662 Facts4Chat",
          location: "Dortmund, Germany (Hybrid)",
          description: "Participated in an eight-member development team focused on improving the factual accuracy of chatbots based on open-source language models. Responsibilities included requirements analysis, technical design, implementation, and testing of a chatbot supported by an Information Retrieval backend. Utilized techniques such as indexing local datasets and using embeddings. Gained practical insights into current challenges and solutions in AI-powered language models and their real-world applications."
        },
        tudortmund2: {
          position: "Linux System Administrator (IRB)",
          location: "Dortmund, Germany",
          description: "Specialized in Bash scripting for task automation. Utilized tools like LDAP servers and Automounter to increase operational efficiency. Implemented virtualization solutions like Docker and applied cloud computing with OpenNebula. Managed database systems such as MySQL and phpMyAdmin to optimize data-related processes."
        },
        kultur: {
          position: "IT System Administrator",
          location: "Bochum, Germany",
          description: "Responsible for administration and maintenance of IT infrastructure, including server and network management, user management (Active Directory), employee support, IT security, and backup solutions. Managed hardware and software and supported IT projects."
        }
      }
    },

    // Projects Section
    projects: {
      title: "Featured Projects",
      subtitle: "Some of my recent work and contributions",
      featuredProjects: "Featured Projects",
      projectsSubtitle: "Some of my recent work and personal projects",
      code: "Code",
      liveDemo: "Live Demo",
      projectData: {
        facts4chat: {
          title: "Facts4Chat - AI Chatbot Enhancement",
          description: "Led an 8-person development team to improve factual accuracy of chatbots based on open-source language models. Implemented information retrieval backend with local data indexing and embeddings for real-world AI applications.",
          category: "AI/ML"
        },
        stubook: {
          title: "StuBook - MERN Stack Book Store",
          description: "Full-stack book store application providing seamless book browsing experience. Features extensive book collection, user reviews and ratings system, and intuitive interface for book discovery and feedback sharing.",
          category: "Full-Stack"
        },
        planvo: {
          title: "Planvo - HR Management Platform",
          description: "Comprehensive HR management solution for managing work hours, shifts, vacation, salaries, and job applicants. Central platform offering flexible solutions for modern HR operations, similar to Personio services.",
          category: "Enterprise"
        }
      }
    },

    // Skills Section
    skills: {
      title: "Technical Skills",
      subtitle: "Technologies and tools I work with",
      technicalSkills: "Technical Skills",
      skillsSubtitle: "Technologies and tools I work with to create innovative solutions",
      categories: {
        programming: "Coding",
        frontend: "Frontend Technologies",
        backend: "Backend & DevOps",
        aiCloud: "AI & Cloud Technologies"
      }
    },

    // Education Section
    education: {
      title: "Education",
      subtitle: "My academic background and qualifications",
      academicBackground: "Academic background and qualifications",
      focus: "Focus",
      keyCourses: "Key Courses",
      educationData: {
        masters: {
          degree: "Master's Degree in Computer Science",
          university: "TU Dortmund University",
          location: "Dortmund, Germany",
          focus: "Software and Services Engineering",
          description: "Currently pursuing Master's degree with focus on Software and Services Engineering. Advanced studies in software architecture, distributed systems, and modern software engineering practices.",
          courses: ["Software Architecture", "Distributed Systems", "Advanced Algorithms", "Software Engineering"]
        },
        bachelors: {
          degree: "Bachelor's Degree in Computer Science",
          university: "TU Dortmund University",
          location: "Dortmund, Germany", 
          focus: "Computer Science Fundamentals",
          description: "Completed Bachelor's degree in Computer Science with thesis on 'Sorting Large Datasets in a Modern Database Management System'. Strong foundation in programming, algorithms, and software development.",
          courses: ["Data Structures & Algorithms", "Database Systems", "Software Engineering", "Mathematics for Computer Science"]
        }
      }
    },

    // Certificates Section
    certificates: {
      title: "Certifications",
      subtitle: "Professional certifications and achievements",
      certifications: "Certifications",
      certificatesSubtitle: "Professional certifications and credentials",
      issued: "Issued",
      verifyCertificate: "Verify Certificate",
      certificateData: {
        aws: {
          name: "AWS Certified AI Practitioner Early Adopter",
          issuer: "Amazon Web Services (AWS)",
          description: "Early adopter certification demonstrating foundational knowledge of AI and machine learning services on AWS platform."
        },
        scrum: {
          name: "Professional Scrum Master I (PSM I)",
          issuer: "Scrum.org",
          description: "Professional certification validating understanding of Scrum framework and ability to support Scrum teams."
        },
        apigee: {
          name: "Developing APIs with Google Cloud's Apigee API Platform",
          issuer: "Google",
          description: "Certification demonstrating proficiency in developing and managing APIs using Google Cloud's Apigee platform."
        }
      }
    },

    // Contact Section
    contact: {
      title: "Get In Touch",
      subtitle: "Let's discuss opportunities and collaboration",
      info: "Contact Information",
      infoDescription: "Feel free to reach out for collaborations or just a friendly hello!",
      email: "Email",
      phone: "Phone",
      location: "Location",
      followMe: "Follow Me",
      sendMessage: "Send a Message",
      messageSent: "Message Sent!",
      thankYou: "Thank you for reaching out. I'll get back to you soon.",
      sendAnother: "Send Another Message",
      sendButton: "Send Message",
      sending: "Sending...",
      placeholders: {
        name: "Your Name",
        email: "Your Email",
        subject: "Subject",
        message: "Your Message"
      }
    },

    // Footer
    footer: {
      copyright: "© 2024 Ahmed Asakrah. All rights reserved."
    }
  },

  de: {
    // Header Section
    greeting: "Hallo, ich bin Ahmed Asakrah 👋",
    tagline: "Software Engineer",
    description: "Full-Stack-Entwickler spezialisiert auf API-Design und KI-gestützte Anwendungen. Erfahren in modernen Web-Technologien, Cloud-Plattformen und Machine Learning-Lösungen. Leidenschaftlich bei der Entwicklung skalierbarer Software-Architekturen und innovativer digitaler Erlebnisse, die die Lücke zwischen Spitzentechnologie und realen Geschäftsanforderungen schließen.",
    downloadCV: "Lebenslauf herunterladen",
    learnMore: "Mehr erfahren",
    scrollDown: "Nach unten scrollen",

    // Navigation
    nav: {
      home: "Startseite",
      about: "Über mich",
      experience: "Erfahrung",
      projects: "Projekte",
      skills: "Fähigkeiten",
      education: "Bildung",
      certificates: "Zertifikate",
      contact: "Kontakt"
    },

    // About Section
    about: {
      title: "Über mich",
      subtitle: "Leidenschaftlicher Software Engineer mit Expertise in Full-Stack-Entwicklung und KI-Technologien",
      professionalSummary: "Berufliche Zusammenfassung",
      summary1: "Ich bin ein engagierter Software Engineer mit Erfahrung in der Entwicklung, Verwaltung und Optimierung von Software-Lösungen in verschiedenen Bereichen. Derzeit arbeite ich bei mVISE AG am APIX-Projekt für die Vodafone Group und konzentriere mich auf RESTful API-Entwicklung mit JavaScript, Node.js und Apigee.",
      summary2: "Meine Expertise umfasst Full-Stack-Entwicklung, KI/ML-Technologien und Cloud Computing. Ich habe praktische Erfahrung mit modernen Entwicklungsframeworks, DevOps-Praktiken und agilen Methoden. Besonders leidenschaftlich bin ich bei KI-gestützten Anwendungen und innovativen Software-Lösungen.",
      summary3: "Wenn ich nicht programmiere, erforsche ich gerne neue Technologien, trage zu Projekten bei und teile Wissen durch Nachhilfe. Ich glaube an kontinuierliches Lernen und strebe immer danach, meine technischen Fähigkeiten zu verbessern und qualitativ hochwertige Lösungen zu liefern.",
      personalDetails: "Persönliche Details",
      location: "Standort",
      age: "Alter",
      languages: "Sprachen",
      availability: "Verfügbarkeit",
      locationValue: "Düsseldorf, Deutschland",
      ageValue: "25 Jahre",
      languagesValue: "Englisch, Deutsch, Arabisch",
      availabilityValue: "Offen für Möglichkeiten",
      features: {
        fullStack: {
          title: "Full-Stack-Entwicklung",
          description: "Erfahren in Frontend- und Backend-Technologien mit Java, JavaScript, Python und React.js."
        },
        ai: {
          title: "KI & Machine Learning",
          description: "Arbeit mit LLMs, PyTorch, TensorFlow und Hugging Face für KI-gestützte Lösungen."
        },
        api: {
          title: "API-Entwicklung",
          description: "Spezialisiert auf die Entwicklung und Optimierung von RESTful APIs mit Node.js und Apigee."
        },
        devops: {
          title: "DevOps & Cloud",
          description: "Kompetent in DevOps-Praktiken mit Jenkins, Docker, AWS und GCP-Technologien."
        }
      }
    },

    // Experience Section
    experience: {
      title: "Berufserfahrung",
      subtitle: "Mein Weg in der Softwareentwicklung und Technologie",
      workExperience: "Berufserfahrung",
      workSubtitle: "Mein beruflicher Werdegang in der Softwareentwicklung und -entwicklung",
      experiences: {
        mvise: {
          position: "Software-Entwickler",
          location: "Düsseldorf, Deutschland (Hybrid)",
          description: "Derzeit bei der Vodafone Group im Rahmen des APIX-Projekts eingesetzt. Verantwortlich für die Entwicklung, Verwaltung und Optimierung von RESTful APIs mit JavaScript, Node.js und Apigee. Implementierung von Sicherheitsmechanismen (z.B. HMAC), Arbeit mit JSON und Nutzung von DevOps-Praktiken und Cloud-Technologien einschließlich AWS Lambda, Amazon S3 und GCP. Arbeit in einer agilen Umgebung mit JIRA und Scrum-Methodologien. Auch beteiligt am WDW-Bosch-Projekt mit Go und AWS Cloud-Services."
        },
        tudortmund1: {
          position: "Projektgruppe 662 Facts4Chat",
          location: "Dortmund, Deutschland (Hybrid)",
          description: "Teilnahme an einem achtköpfigen Entwicklungsteam zur Verbesserung der faktischen Genauigkeit von Chatbots basierend auf Open-Source-Sprachmodellen. Zu den Aufgaben gehörten Anforderungsanalyse, technisches Design, Implementierung und Testen eines Chatbots mit Information Retrieval-Backend. Nutzung von Techniken wie Indexierung lokaler Datensätze und Verwendung von Embeddings. Praktische Einblicke in aktuelle Herausforderungen und Lösungen bei KI-gestützten Sprachmodellen und deren realen Anwendungen."
        },
        tudortmund2: {
          position: "Linux System Administrator (IRB)",
          location: "Dortmund, Deutschland",
          description: "Spezialisiert auf Bash-Scripting für Aufgabenautomatisierung. Nutzung von Tools wie LDAP-Servern und Automounter zur Steigerung der Betriebseffizienz. Implementierung von Virtualisierungslösungen wie Docker und Anwendung von Cloud Computing mit OpenNebula. Verwaltung von Datenbanksystemen wie MySQL und phpMyAdmin zur Optimierung datenbezogener Prozesse."
        },
        kultur: {
          position: "IT-Systemadministrator",
          location: "Bochum, Deutschland",
          description: "Verantwortlich für die Administration und Wartung der IT-Infrastruktur, einschließlich Server- und Netzwerkverwaltung, Benutzerverwaltung (Active Directory), Mitarbeiterunterstützung, IT-Sicherheit und Backup-Lösungen. Verwaltung von Hard- und Software und Unterstützung von IT-Projekten."
        }
      }
    },

    // Projects Section
    projects: {
      title: "Ausgewählte Projekte",
      subtitle: "Einige meiner neuesten Arbeiten und Beiträge",
      featuredProjects: "Ausgewählte Projekte",
      projectsSubtitle: "Einige meiner neuesten Arbeiten und persönlichen Projekte",
      code: "Code",
      liveDemo: "Live Demo",
      projectData: {
        facts4chat: {
          title: "Facts4Chat - KI-Chatbot-Verbesserung",
          description: "Leitete ein 8-köpfiges Entwicklungsteam zur Verbesserung der faktischen Genauigkeit von Chatbots basierend auf Open-Source-Sprachmodellen. Implementierte Information Retrieval-Backend mit lokaler Datenindexierung und Embeddings für reale KI-Anwendungen.",
          category: "KI/ML"
        },
        stubook: {
          title: "StuBook - MERN Stack Buchhandlung",
          description: "Full-Stack-Buchhandlungsanwendung mit nahtlosem Buchbrowsing-Erlebnis. Bietet umfangreiche Buchsammlung, Benutzerbewertungs- und Bewertungssystem sowie intuitive Benutzeroberfläche für Buchentdeckung und Feedback-Austausch.",
          category: "Full-Stack"
        },
        planvo: {
          title: "Planvo - HR-Management-Plattform",
          description: "Umfassende HR-Management-Lösung zur Verwaltung von Arbeitszeiten, Schichten, Urlaub, Gehältern und Stellenbewerbern. Zentrale Plattform mit flexiblen Lösungen für moderne HR-Operationen, ähnlich wie Personio-Services.",
          category: "Unternehmen"
        }
      }
    },

    // Skills Section
    skills: {
      title: "Technische Fähigkeiten",
      subtitle: "Technologien und Tools, mit denen ich arbeite",
      technicalSkills: "Technische Fähigkeiten",
      skillsSubtitle: "Technologien und Tools, mit denen ich arbeite, um innovative Lösungen zu erstellen",
      categories: {
        programming: "Programmierung",
        frontend: "Frontend-Technologien",
        backend: "Backend & DevOps",
        aiCloud: "KI & Cloud-Technologien"
      }
    },

    // Education Section
    education: {
      title: "Bildung",
      subtitle: "Mein akademischer Hintergrund und Qualifikationen",
      academicBackground: "Akademischer Hintergrund und Qualifikationen",
      focus: "Schwerpunkt",
      keyCourses: "Wichtige Kurse",
      educationData: {
        masters: {
          degree: "Master-Abschluss in Informatik",
          university: "TU Dortmund University",
          location: "Dortmund, Deutschland",
          focus: "Software- und Service-Engineering",
          description: "Derzeit Master-Studium mit Schwerpunkt Software- und Service-Engineering. Fortgeschrittene Studien in Softwarearchitektur, verteilten Systemen und modernen Software-Engineering-Praktiken.",
          courses: ["Softwarearchitektur", "Verteilte Systeme", "Erweiterte Algorithmen", "Software Engineering"]
        },
        bachelors: {
          degree: "Bachelor-Abschluss in Informatik",
          university: "TU Dortmund University",
          location: "Dortmund, Deutschland",
          focus: "Informatik-Grundlagen",
          description: "Bachelor-Abschluss in Informatik mit Abschlussarbeit über 'Sortierung großer Datensätze in einem modernen Datenbank-Management-System'. Solide Grundlage in Programmierung, Algorithmen und Softwareentwicklung.",
          courses: ["Datenstrukturen & Algorithmen", "Datenbanksysteme", "Software Engineering", "Mathematik für Informatik"]
        }
      }
    },

    // Certificates Section
    certificates: {
      title: "Zertifizierungen",
      subtitle: "Berufliche Zertifizierungen und Errungenschaften",
      certifications: "Zertifizierungen",
      certificatesSubtitle: "Berufliche Zertifizierungen und Qualifikationen",
      issued: "Ausgestellt",
      verifyCertificate: "Zertifikat verifizieren",
      certificateData: {
        aws: {
          name: "AWS Certified AI Practitioner Early Adopter",
          issuer: "Amazon Web Services (AWS)",
          description: "Early Adopter-Zertifizierung, die grundlegende Kenntnisse von KI- und Machine Learning-Services auf der AWS-Plattform demonstriert."
        },
        scrum: {
          name: "Professional Scrum Master I (PSM I)",
          issuer: "Scrum.org",
          description: "Berufliche Zertifizierung, die das Verständnis des Scrum-Frameworks und die Fähigkeit zur Unterstützung von Scrum-Teams validiert."
        },
        apigee: {
          name: "Developing APIs with Google Cloud's Apigee API Platform",
          issuer: "Google",
          description: "Zertifizierung, die Kompetenz in der Entwicklung und Verwaltung von APIs mit Google Clouds Apigee-Plattform demonstriert."
        }
      }
    },

    // Contact Section
    contact: {
      title: "Kontakt aufnehmen",
      subtitle: "Lassen Sie uns über Möglichkeiten und Zusammenarbeit sprechen",
      info: "Kontaktinformationen",
      infoDescription: "Zögern Sie nicht, mich für Kooperationen oder einfach ein freundliches Hallo zu kontaktieren!",
      email: "E-Mail",
      phone: "Telefon",
      location: "Standort",
      followMe: "Folgen Sie mir",
      sendMessage: "Nachricht senden",
      messageSent: "Nachricht gesendet!",
      thankYou: "Vielen Dank für Ihre Kontaktaufnahme. Ich werde mich bald bei Ihnen melden.",
      sendAnother: "Weitere Nachricht senden",
      sendButton: "Nachricht senden",
      sending: "Wird gesendet...",
      placeholders: {
        name: "Ihr Name",
        email: "Ihre E-Mail",
        subject: "Betreff",
        message: "Ihre Nachricht"
      }
    },

    // Footer
    footer: {
      copyright: "© 2024 Ahmed Asakrah. Alle Rechte vorbehalten."
    }
  }
}; 